import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useChangeAfterFetching } from '@/hooks/useChangeAfterFetching';
import { useLatestBlock } from '@/hooks/useLatestBlock';
import { resolveVaultDataAtBlock } from './vaultV1Accrual.helpers';

/**
 * Fetches and polls the on-chain V1 (MetaMorpho) accrual vault data every 10s.
 * The returned `vault` entity builds deposit/withdraw/redeem transactions and
 * `vaultData` (an `AccrualVault`) exposes `toAssets` / `maxDeposit` / `maxWithdraw`.
 */
export function useVaultV1(vaultAddress) {
  const $ = _c(29);
  const {
    viemClient,
    targetChainId,
    account
  } = useWeb3Context();
  let t0;
  if ($[0] !== targetChainId || $[1] !== vaultAddress || $[2] !== viemClient.morpho) {
    t0 = viemClient.morpho.vaultV1(vaultAddress, targetChainId);
    $[0] = targetChainId;
    $[1] = vaultAddress;
    $[2] = viemClient.morpho;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const vault = t0;
  let t1;
  if ($[4] !== account.isConnected) {
    t1 = {
      enabled: account.isConnected
    };
    $[4] = account.isConnected;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: block,
    isLoading: isBlockLoading,
    isFetching: isFetchingBlock,
    error: blockError
  } = useLatestBlock(targetChainId, t1);
  let blockNumber;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[6] !== account.isConnected || $[7] !== block?.number || $[8] !== targetChainId || $[9] !== vault || $[10] !== vaultAddress) {
    blockNumber = block?.number?.toString();
    t5 = useQuery;
    t2 = ["vaultV1Data", vaultAddress, targetChainId, blockNumber];
    t3 = () => vault.getData({
      blockNumber: BigInt(blockNumber)
    });
    t4 = account.isConnected && isDefined(blockNumber);
    $[6] = account.isConnected;
    $[7] = block?.number;
    $[8] = targetChainId;
    $[9] = vault;
    $[10] = vaultAddress;
    $[11] = blockNumber;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    blockNumber = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t6 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      placeholderData: keepPreviousData,
      staleTime: Infinity,
      gcTime: 20000
    };
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const {
    data: rawVaultData,
    isLoading,
    isFetching,
    error
  } = t5(t6);
  let t7;
  bb0: {
    if (!rawVaultData || !isDefined(block)) {
      t7 = undefined;
      break bb0;
    }
    let t8;
    if ($[20] !== block.timestamp || $[21] !== rawVaultData) {
      t8 = resolveVaultDataAtBlock(rawVaultData, block.timestamp);
      $[20] = block.timestamp;
      $[21] = rawVaultData;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    t7 = t8;
  }
  const vaultData = t7;
  const memoizedBlockError = useChangeAfterFetching(blockError, isFetchingBlock);
  const memoizedError = useChangeAfterFetching(error, isFetching);
  const t8 = isLoading || isBlockLoading;
  const t9 = memoizedBlockError || memoizedError;
  let t10;
  if ($[23] !== blockNumber || $[24] !== t8 || $[25] !== t9 || $[26] !== vault || $[27] !== vaultData) {
    t10 = {
      vault,
      vaultData,
      vaultDataBlockNumber: blockNumber,
      isLoading: t8,
      error: t9
    };
    $[23] = blockNumber;
    $[24] = t8;
    $[25] = t9;
    $[26] = vault;
    $[27] = vaultData;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}